import { MIN_PICKS, MIN_TOP } from './data'
import {
  clearLeadMagnetUnlockCookie,
  hasLeadMagnetUnlockCookie,
  setLeadMagnetUnlockCookie,
} from '../../../utils/lead-magnet-unlock'
import type { SpendCheckState } from '../types'

/** Cookie `worthwhile-spend-check_unlocked` — same convention as career-break / lead magnets. */
export const UNLOCK_SLUG = 'worthwhile-spend-check'

const STATE_KEY = 'worthwhile-spend-check_state'

/** Cache for useSyncExternalStore: getSnapshot must return Object.is-stable values. */
let snapshotCache: { raw: string | null; state: SpendCheckState | null } | null = null

function invalidateSnapshotCache(): void {
  snapshotCache = null
}

function parseUnlockedState(raw: string | null): SpendCheckState | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as SpendCheckState
    if (!Array.isArray(parsed.imp) || parsed.imp.length < MIN_PICKS) return null
    if (!Array.isArray(parsed.top) || parsed.top.length < MIN_TOP) return null
    return { ...parsed, screen: 'result' }
  } catch {
    return null
  }
}

export function hasUnlockCookie(): boolean {
  return hasLeadMagnetUnlockCookie(UNLOCK_SLUG)
}

export function setUnlockCookie(): void {
  setLeadMagnetUnlockCookie(UNLOCK_SLUG)
  invalidateSnapshotCache()
}

export function clearUnlockCookie(): void {
  clearLeadMagnetUnlockCookie(UNLOCK_SLUG)
  invalidateSnapshotCache()
}

export function saveUnlockedState(state: SpendCheckState): void {
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({ ...state, screen: 'result' }))
  } catch {
    // Storage can be blocked — cookie still prevents a second lead submit UI path.
  }
  invalidateSnapshotCache()
}

/**
 * Stable unlock snapshot for `useSyncExternalStore`.
 * Parsing sessionStorage on every read allocates a new object and trips React #185.
 */
export function getUnlockedSnapshot(): SpendCheckState | null {
  if (!hasUnlockCookie()) {
    invalidateSnapshotCache()
    return null
  }

  let raw: string | null
  try {
    raw = sessionStorage.getItem(STATE_KEY)
  } catch {
    invalidateSnapshotCache()
    return null
  }

  if (snapshotCache && snapshotCache.raw === raw) return snapshotCache.state

  const state = parseUnlockedState(raw)
  snapshotCache = { raw, state }
  return state
}

export function loadUnlockedState(): SpendCheckState | null {
  return getUnlockedSnapshot()
}

export function clearUnlockedState(): void {
  try {
    sessionStorage.removeItem(STATE_KEY)
  } catch {
    // ignore
  }
  invalidateSnapshotCache()
}
